/**
 * useActionEvents Hook
 *
 * Custom hook for handling bulk action-related WebSocket events.
 * Manages action history tracking, bulk operation status updates, and credential state changes.
 *
 * Phase 3C: Action Events Extraction
 *
 * Events handled:
 * - lastActionCreated: Track new actions created on devices
 * - lastActionUpdated: Track action status updates (RESOLVED, REJECTED, etc.)
 *
 * @module hooks/useActionEvents
 */

import { useCallback } from "react";
import { useCredentialsStore, useUIStore } from "../stores";
import { useDevicesStore } from "../stores";
import logger from "../utils/logger";

/**
 * Helper function to create pending key for device-credential pairs
 * Format: "deviceId:credentialId"
 *
 * @param {string} deviceId - The device ID
 * @param {string} credentialId - The credential ID
 * @returns {string} Combined key
 */
const makePendingKey = (deviceId, credentialId) =>
  `${deviceId}:${credentialId}`;

/**
 * Helper function to parse pending key into components
 *
 * @param {string} key - The combined key
 * @returns {Object} Object with deviceId and credentialId
 */
const parsePendingKey = (key) => {
  if (!key || typeof key !== "string") {
    return { deviceId: null, credentialId: null };
  }
  const [deviceId, credentialId] = key.split(":");
  return { deviceId, credentialId };
};

/**
 * Custom hook for handling action-related WebSocket events
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.currentDeviceId - Currently selected device ID
 * @param {Object} params.lastActionRef - Ref to last action for comparison
 * @param {Function} params.setActionHistory - Function to update action history
 * @param {Function} params.setBulkActionStatuses - Function to update bulk action statuses
 * @param {Function} params.updateBulkActionTimestamp - Function to update action timestamps
 * @param {Function} params.showNotification - Function to show notifications
 * @param {number} params.MAX_HISTORY_IN_MEMORY - Maximum number of actions to keep in history
 * @param {Function} params.checkResetBulkLockdownTracking - Function to check/reset bulk lockdown tracking
 * @param {Function} params.setLastLockdownEventTime - Function to set last lockdown event time
 * @param {Function} params.updateDeviceTracking - Function to update device tracking (from bulkOperationsStore)
 * @param {Function} params.removeStatusFromStore - Function to remove credential status from localStorage
 * @param {Function} params.authenticatedFetch - Authenticated fetch wrapper for API calls
 * @param {string} params.installationId - Current installation ID
 *
 * @returns {Object} Object with action event handler functions
 * @returns {Function} handleLastActionCreated - Handler for lastActionCreated events
 * @returns {Function} handleLastActionUpdated - Handler for lastActionUpdated events
 */
export const useActionEvents = ({
  currentDeviceId,
  lastActionRef,
  setActionHistory,
  setBulkActionStatuses,
  updateBulkActionTimestamp,
  showNotification,
  MAX_HISTORY_IN_MEMORY,
  checkResetBulkLockdownTracking,
  setLastLockdownEventTime,
  updateDeviceTracking,
  removeStatusFromStore,
  authenticatedFetch,
  installationId,
  fetchDeviceState,
}) => {
  // Access Zustand stores
  const {
    setCredentials,
    setPendingDeletionCredentials,
    setPendingAdditionCredentials,
    setDevicesWithPendingDeletions,
  } = useCredentialsStore();

  const { updateDeviceState } = useDevicesStore();
  const openDeviceUpdateModal = useUIStore(
    (state) => state.openDeviceUpdateModal,
  );
  const markDeviceUpdateReady = useUIStore(
    (state) => state.markDeviceUpdateReady,
  );

  /**
   * Handler for lastActionCreated events
   *
   * Tracks new actions created on the currently selected device.
   * Updates action history with the latest action, maintaining chronological order.
   *
   * @param {Object} message - WebSocket message
   */
  const handleLastActionCreated = useCallback(
    (message) => {
      if (message.deviceId === currentDeviceId) {
        const incomingAction = message.lastAction;
        setActionHistory((prevHistory) => {
          const currentLatest = prevHistory[0];
          if (
            !currentLatest ||
            !currentLatest.createdAt ||
            (incomingAction.createdAt &&
              new Date(incomingAction.createdAt) >=
                new Date(currentLatest.createdAt))
          ) {
            const existingIndex = prevHistory.findIndex(
              (a) => a.actionId === incomingAction.actionId,
            );
            if (existingIndex !== -1) {
              if (
                prevHistory[existingIndex].actionStatus === "RESOLVED" ||
                prevHistory[existingIndex].actionStatus === "REJECTED"
              ) {
                return prevHistory;
              } else {
                const newHistory = [...prevHistory];
                newHistory[existingIndex] = incomingAction;
                return newHistory;
              }
            } else {
              return [incomingAction, ...prevHistory].slice(
                0,
                MAX_HISTORY_IN_MEMORY,
              );
            }
          } else {
            return prevHistory;
          }
        });
      }
    },
    [currentDeviceId, setActionHistory, MAX_HISTORY_IN_MEMORY],
  );

  /**
   * Handler for lastActionUpdated events
   *
   * Tracks action status updates (RESOLVED, REJECTED, IN_PROGRESS).
   * Handles credential deletion/addition cleanup and bulk operation tracking.
   *
   * @param {Object} message - WebSocket message
   */
  const handleLastActionUpdated = useCallback(
    (message) => {
      // Extract action data from message
      // Note: credentialId and actionType are in the lastAction object if present
      const incomingAction = {
        actionId: message.actionId,
        actionStatus: message.actionStatus,
        lastUpdatedAt: message.lastUpdatedAt,
        deviceId: message.deviceId,
        ...message.lastAction, // Merge lastAction properties (includes actionType, credentialId, etc.)
      };

      logger.log(
        `[lastActionUpdated] 📬 Received event for device ${message.deviceId}:`,
        {
          actionStatus: incomingAction.actionStatus,
          actionType: incomingAction.actionType,
          credentialId: incomingAction.credentialId,
          deviceId: incomingAction.deviceId,
          actionId: incomingAction.actionId,
        },
      );

      // Store action timestamps for bulk operations (for accurate timing calculations)
      if (
        incomingAction.actionId &&
        (incomingAction.createdAt || incomingAction.cloudCreatedAt)
      ) {
        const timestamps = {
          createdAt: incomingAction.createdAt,
          cloudCreatedAt: incomingAction.cloudCreatedAt,
          cloudUpdatedAt:
            incomingAction.cloudUpdatedAt || message.cloudUpdatedAt,
          lastUpdatedAt: incomingAction.lastUpdatedAt || message.lastUpdatedAt,
        };

        logger.log(
          `[lastActionUpdated] 📅 Storing timestamps for action ${incomingAction.actionId}:`,
          timestamps,
        );

        updateBulkActionTimestamp(incomingAction.actionId, timestamps);
      } else if (incomingAction.actionId) {
        logger.warn(
          `[lastActionUpdated] ⚠️ No timestamps available for action ${incomingAction.actionId}`,
          {
            createdAt: incomingAction.createdAt,
            cloudCreatedAt: incomingAction.cloudCreatedAt,
            hasLastAction: !!message.lastAction,
          },
        );
      }

      // Update bulk action modal status for ANY action with resolved/rejected status
      // This handles bulk lockdown operations where actionType might not be present
      if (
        incomingAction.actionId &&
        incomingAction.actionStatus &&
        (incomingAction.actionStatus === "RESOLVED" ||
          incomingAction.actionStatus === "REJECTED" ||
          incomingAction.actionStatus === "PENDING")
      ) {
        logger.log(
          `[lastActionUpdated] 📊 Updating bulk action status - ActionID: ${incomingAction.actionId}, Status: ${incomingAction.actionStatus}`,
        );
        setBulkActionStatuses((prevStatuses) => ({
          ...prevStatuses,
          [incomingAction.actionId]: incomingAction.actionStatus,
        }));
      }

      // Enhanced logging for lockdown actions
      if (
        incomingAction.actionType === "FREEZE" ||
        incomingAction.actionType === "UNFREEZE"
      ) {
        logger.log(
          `[lastActionUpdated] 🔒 LOCKDOWN ACTION - Device: ${message.deviceId}, Action: ${incomingAction.actionId}, Status: ${incomingAction.actionStatus}`,
        );

        // Track this update using bulkOperationsStore (Phase 3D PR #1)
        checkResetBulkLockdownTracking();
        setLastLockdownEventTime(Date.now());
        updateDeviceTracking(message.deviceId, {
          lastActionUpdated: true,
          actionStatus: incomingAction.actionStatus,
        });
      }

      // Handle credential deletion when action is RESOLVED (for any device)
      if (
        incomingAction.actionStatus === "RESOLVED" &&
        incomingAction.actionType === "DELETE_CREDENTIAL" &&
        incomingAction.credentialId
      ) {
        logger.log(
          `[lastActionUpdated] 🎯 Credential deletion resolved for ${incomingAction.credentialId} on device ${incomingAction.deviceId}, removing from UI`,
        );
        // Remove device-credential pair from pending deletion set
        setPendingDeletionCredentials((prev) => {
          logger.log(
            `[lastActionUpdated] 📝 Inside setPendingDeletionCredentials callback`,
          );
          logger.log(`[lastActionUpdated] - prev set:`, Array.from(prev));

          const newSet = new Set(prev);
          // If deviceId is available, remove only that specific device-credential pair
          if (incomingAction.deviceId) {
            const pendingKey = makePendingKey(
              incomingAction.deviceId,
              incomingAction.credentialId,
            );
            const wasDeleted = newSet.delete(pendingKey);
            logger.log(
              `[lastActionUpdated] ✅ Removed ${pendingKey} from pending deletions (existed: ${wasDeleted})`,
            );
          } else {
            // Fallback: remove all entries for this credential across all devices
            const keysToDelete = [...newSet].filter((key) => {
              if (!key || typeof key !== "string") return false;
              return (
                parsePendingKey(key).credentialId ===
                incomingAction.credentialId
              );
            });
            keysToDelete.forEach((key) => newSet.delete(key));
          }

          logger.log(
            `[lastActionUpdated] - Updated pending set:`,
            Array.from(newSet),
          );

          // Check if this device has any other pending deletions AFTER removing this one
          if (incomingAction.deviceId) {
            const otherPendingKeys = Array.from(newSet).filter(
              (key) =>
                parsePendingKey(key).deviceId === incomingAction.deviceId,
            );
            const hasOtherPendingDeletions = otherPendingKeys.length > 0;

            logger.log(
              `[lastActionUpdated] 🔍 Checking for other pending deletions on device ${incomingAction.deviceId}:`,
            );
            logger.log(
              `[lastActionUpdated] - Other pending keys:`,
              otherPendingKeys,
            );
            logger.log(
              `[lastActionUpdated] - hasOtherPendingDeletions: ${hasOtherPendingDeletions}`,
            );

            if (!hasOtherPendingDeletions) {
              // No more pending deletions for this device, re-enable delete buttons
              logger.log(
                `[lastActionUpdated] 🟢 No more pending deletions for device ${incomingAction.deviceId}, re-enabling delete buttons NOW`,
              );
              setDevicesWithPendingDeletions((prevDevices) => {
                logger.log(
                  `[lastActionUpdated] 📝 Inside setDevicesWithPendingDeletions callback`,
                );
                logger.log(
                  `[lastActionUpdated] - prevDevices:`,
                  Array.from(prevDevices),
                );

                const updatedDevices = new Set(prevDevices);
                const wasInSet = updatedDevices.delete(incomingAction.deviceId);
                logger.log(
                  `[lastActionUpdated] ✅ Removed device ${incomingAction.deviceId} from devicesWithPendingDeletions (existed: ${wasInSet})`,
                );
                logger.log(
                  `[lastActionUpdated] - Updated devices set:`,
                  Array.from(updatedDevices),
                );
                logger.log(
                  `[lastActionUpdated] 🎉 STATE UPDATE COMPLETE - Buttons should re-enable on next render`,
                );
                return updatedDevices;
              });
            } else {
              logger.log(
                `[lastActionUpdated] 🔴 Device ${incomingAction.deviceId} still has ${otherPendingKeys.length} pending deletion(s), keeping delete buttons disabled`,
              );
            }
          }

          return newSet;
        });

        // Only update credentials list and storage for current device
        if (message.deviceId === currentDeviceId) {
          setCredentials((prevCreds) =>
            prevCreds.filter((c) => c.id !== incomingAction.credentialId),
          );
          removeStatusFromStore(incomingAction.credentialId);
        }
      }

      // Handle credential addition when action is RESOLVED (for any device)
      if (
        incomingAction.actionStatus === "RESOLVED" &&
        incomingAction.actionType === "PROVISION_CREDENTIAL" &&
        incomingAction.credentialId
      ) {
        logger.log(
          `Credential addition resolved for ${incomingAction.credentialId} on device ${incomingAction.deviceId}, removing from pending UI`,
        );
        // Remove device-credential pair from pending addition set
        setPendingAdditionCredentials((prev) => {
          const newSet = new Set(prev);
          logger.log(
            `[PENDING CLEANUP] Before removal, pending additions:`,
            Array.from(prev),
          );
          // If deviceId is available, remove only that specific device-credential pair
          if (incomingAction.deviceId) {
            const pendingKey = makePendingKey(
              incomingAction.deviceId,
              incomingAction.credentialId,
            );
            logger.log(
              `[PENDING CLEANUP] Attempting to delete key: ${pendingKey}`,
            );
            const deleted = newSet.delete(pendingKey);
            logger.log(
              `[PENDING CLEANUP] Delete result: ${deleted}, After removal, pending additions:`,
              Array.from(newSet),
            );
          } else {
            // Fallback: remove all entries for this credential across all devices
            const keysToDelete = [...newSet].filter((key) => {
              if (!key || typeof key !== "string") return false;
              return (
                parsePendingKey(key).credentialId ===
                incomingAction.credentialId
              );
            });
            logger.log(
              `[PENDING CLEANUP] Fallback - keys to delete:`,
              keysToDelete,
            );
            keysToDelete.forEach((key) => newSet.delete(key));
          }
          return newSet;
        });
      }

      // Handle firmware update action completion
      // Only notify if the device belongs to the currently active installation.
      if (
        incomingAction.actionName === "FirmwareV1InstallLatestFirmwareUpdate" &&
        useDevicesStore
          .getState()
          .devices.some((d) => d.deviceId === message.deviceId)
      ) {
        if (incomingAction.actionStatus === "RESOLVED") {
          logger.log(
            `[lastActionUpdated] 🔄 Firmware update resolved for device ${message.deviceId}, opening update modal`,
          );
          // Open modal to notify user and let them refresh when ready
          openDeviceUpdateModal("firmware", message.deviceId);

          // Firmware updates take 10+ minutes. The device typically comes back
          // ONLINE before Yonomi marks the action as RESOLVED, so the normal
          // connectivityStatus === "ONLINE" path in handleDeviceStateUpdate
          // fires while the modal isn't open yet and gets dropped. Check the
          // current device state and immediately mark ready if already ONLINE.
          const currentDeviceState = useDevicesStore.getState().deviceStates[message.deviceId];
          if (currentDeviceState?.connectivityStatus === "ONLINE") {
            logger.log(
              `[lastActionUpdated] 🔄 Device already ONLINE when firmware resolved — marking ready immediately`,
            );
            markDeviceUpdateReady(message.deviceId);
          }
        } else if (incomingAction.actionStatus === "REJECTED") {
          logger.log(
            `[lastActionUpdated] ❌ Firmware update rejected for device ${message.deviceId}`,
          );
          showNotification({
            message: `Firmware update failed for device.`,
            type: "error",
          });
        }
      }

      // Handle WiFi settings change completion
      // Only notify if the device belongs to the currently active installation —
      // the WebSocket is org-scoped so events from other sites arrive here too.
      if (
        incomingAction.actionName ===
          "WifiSettingsV1ConnectStandardAccessPoint" &&
        incomingAction.actionStatus === "RESOLVED" &&
        useDevicesStore
          .getState()
          .devices.some((d) => d.deviceId === message.deviceId)
      ) {
        logger.log(
          `[lastActionUpdated] 📶 WiFi settings updated for device ${message.deviceId}, opening update modal`,
        );
        // Open modal to notify user and let them refresh when ready
        openDeviceUpdateModal("wifi", message.deviceId);
      }

      // Handle all device settings change outcomes
      if (
        (incomingAction.actionName === "CredentialsSettingsV1SetCredentialsSettings" ||
         incomingAction.actionName === "ConnectivityEventSettingsV1SetConnectivityEventSettings" ||
         incomingAction.actionName === "LockEventSettingsV1SetLockSettings" ||
         incomingAction.actionName === "DoorPositionEventSettingsV1SetDoorEventSettings" ||
         incomingAction.actionName === "ExtendedAutoRelockDelaySettingsV1SetExtendedAutoRelockDelay" ||
         incomingAction.actionName === "AutoRelockDelaySettingsV1SetAutoRelockDelay" ||
         incomingAction.actionName === "BeeperSettingsV1SetBeeperSettings" ||
         incomingAction.actionName === "BleCredentialSettingsV1SetBleCredentialSettings" ||
         incomingAction.actionName === "ReaderSettingsV1SetReaderSettings" ||
         incomingAction.actionName === "WifiSettingsV1SetWifiConnectionSettings" ||
         incomingAction.actionName === "TimeZoneSettingsV1SetTimeZoneOffset") &&
        useDevicesStore.getState().devices.some((d) => d.deviceId === message.deviceId)
      ) {
        if (incomingAction.actionStatus === "REJECTED") {
          logger.log(
            `[lastActionUpdated] ❌ Credential settings change rejected for device ${message.deviceId}`,
          );
          showNotification({
            message: "Setting change was rejected by the device and was not applied.",
            type: "error",
          });
          if (message.deviceId === currentDeviceId && fetchDeviceState) {
            fetchDeviceState(message.deviceId);
          }
        } else if (incomingAction.actionStatus === "RESOLVED") {
          logger.log(
            `[lastActionUpdated] ✅ Credential settings change resolved for device ${message.deviceId}, refreshing device state`,
          );
          if (message.deviceId === currentDeviceId && fetchDeviceState) {
            fetchDeviceState(message.deviceId);
          }
        }
      }

      // Update action history and UI state only for the currently selected device
      if (message.deviceId === currentDeviceId) {
        const previousState = lastActionRef.current;

        setActionHistory((prevHistory) => {
          const existingIndex = prevHistory.findIndex(
            (a) => a.actionId === incomingAction.actionId,
          );
          let newHistory;

          if (existingIndex !== -1) {
            if (
              !prevHistory[existingIndex].lastUpdatedAt ||
              (incomingAction.lastUpdatedAt &&
                new Date(incomingAction.lastUpdatedAt) >=
                  new Date(prevHistory[existingIndex].lastUpdatedAt))
            ) {
              newHistory = [...prevHistory];
              newHistory[existingIndex] = incomingAction;
            } else {
              return prevHistory;
            }
          } else {
            const currentLatest = prevHistory[0];
            if (
              !currentLatest ||
              !currentLatest.createdAt ||
              (incomingAction.lastUpdatedAt &&
                new Date(incomingAction.lastUpdatedAt) >=
                  new Date(currentLatest.createdAt))
            ) {
              newHistory = [incomingAction, ...prevHistory];
            } else {
              return prevHistory;
            }
          }
          return newHistory.slice(0, MAX_HISTORY_IN_MEMORY);
        });

        // Handle notifications for action state changes
        if (
          previousState &&
          previousState.actionId === incomingAction.actionId
        ) {
          const newStatus = incomingAction.actionStatus?.toLowerCase();
          const oldStatus = previousState.actionStatus?.toLowerCase();
          if (
            (newStatus === "resolved" || newStatus === "rejected") &&
            oldStatus !== newStatus
          ) {
            showNotification({
              message: `Action ${newStatus}.`,
              type: newStatus === "resolved" ? "success" : "error",
            });
          }
        } else if (
          !previousState &&
          incomingAction.actionStatus &&
          (incomingAction.actionStatus === "RESOLVED" ||
            incomingAction.actionStatus === "REJECTED")
        ) {
          showNotification({
            message: `Action ${incomingAction.actionStatus.toLowerCase()}.`,
            type:
              incomingAction.actionStatus === "RESOLVED" ? "success" : "error",
          });
        }
      }

      // Update bulk action status tracking if this action is part of a bulk operation
      setBulkActionStatuses((prevStatuses) => {
        logger.log(`[lastActionUpdated] Checking bulk action status update:`, {
          actionId: incomingAction.actionId,
          newStatus: incomingAction.actionStatus,
          existsInPrev: !!prevStatuses[incomingAction.actionId],
          prevStatus: prevStatuses[incomingAction.actionId],
          allPrevActionIds: Object.keys(prevStatuses),
        });

        if (prevStatuses[incomingAction.actionId]) {
          // Action exists - update it
          logger.log(
            `[lastActionUpdated] ✅ Updating bulk action status for ${incomingAction.actionId}: ${incomingAction.actionStatus}`,
          );
          return {
            ...prevStatuses,
            [incomingAction.actionId]: incomingAction.actionStatus,
          };
        }

        // Action not found yet - check if we have any actions tracked
        if (Object.keys(prevStatuses).length === 0) {
          // No bulk operation initialized yet - this UPDATED event arrived before API response
          // Add it optimistically so we don't lose the RESOLVED status
          logger.log(
            `[lastActionUpdated] 📝 Bulk operation not initialized yet, adding action ${incomingAction.actionId} with status ${incomingAction.actionStatus}`,
          );
          return {
            ...prevStatuses,
            [incomingAction.actionId]: incomingAction.actionStatus,
          };
        }

        // Bulk operation exists but this action isn't part of it (not in current operation)
        logger.log(
          `[lastActionUpdated] ⚠️  Action ${
            incomingAction.actionId
          } not found in current bulk operation (${
            Object.keys(prevStatuses).length
          } actions tracked)`,
        );
        return prevStatuses;
      });

      // On REJECTED credential settings action, do a full sync to correct stale optimistic UI
      if (
        incomingAction.actionStatus === "REJECTED" &&
        incomingAction.actionType === "CredentialsSettingsV1SetCredentialsSettings"
      ) {
        fetchDeviceState(incomingAction.deviceId); // fire-and-forget, no await
      }
    },
    [
      currentDeviceId,
      lastActionRef,
      setActionHistory,
      setBulkActionStatuses,
      showNotification,
      MAX_HISTORY_IN_MEMORY,
      checkResetBulkLockdownTracking,
      setLastLockdownEventTime,
      updateDeviceTracking,
      removeStatusFromStore,
      openDeviceUpdateModal,
      markDeviceUpdateReady,
      setCredentials,
      setPendingDeletionCredentials,
      setPendingAdditionCredentials,
      setDevicesWithPendingDeletions,
      updateBulkActionTimestamp,
      fetchDeviceState,
    ],
  );

  return {
    handleLastActionCreated,
    handleLastActionUpdated,
  };
};
